import React, { useEffect, useState } from 'react';
import GlobalStyles from '@mui/material/GlobalStyles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { motion } from 'framer-motion';
import { 
  Building2, UserRound, Phone, MapPin, Clock, Star, 
  CheckCircle2, Sparkles, Stethoscope, Play, ChevronDown, Check,
  Activity, HeartPulse, Pill, Brain, Bone, Eye, Award, ArrowRight, Video, MessageSquareQuote, HelpCircle, ShieldCheck,
  CreditCard, Banknote, CalendarCheck
} from 'lucide-react';

import hospitalApiService from '../../services/hospitalApiService';
import VideoConsultModal from '../../components/VideoConsultModal';

// ─── Color tokens (V2) ──────────────────────────────────────────────────────
const C = {
  navy:    '#0A0A0A',
  navy2:   '#171717',
  navy3:   '#262626',
  teal:    '#2563EB',
  tealLt:  '#EFF6FF',
  tealMid: '#3B82F6',
  bg:      '#FAFAFA',
  border:  'rgba(0, 0, 0, 0.08)',
  text:    '#171717',
  text2:   '#525252',
  text3:   '#737373',
  white:   '#FFFFFF',
  gold:    '#EAB308',
  goldLt:  '#FFFBEB'
};

const fadeUp: any = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } } };
const stagger: any = { visible: { transition: { staggerChildren: 0.1 } } };

// ─── Types ──────────────────────────────────────────────────────────────────
interface TestimonialSample { quote: string; name: string; age?: number; procedure?: string; facility?: string; star_rating?: number; visit_date?: string; is_pinned?: boolean; verified?: boolean; }
interface AccredItem { id: number; name: string; type?: string | null; description?: string; emoji?: string | null; active: boolean; }
interface DepartmentItem { id: number; name: string; emoji?: string; doctor_count?: number; procedure_count?: number; testimonial_count?: number; media_count?: number; }
interface FacilityItem { id: number; name: string; emoji?: string; description?: string; }
interface InsurancePartnerItem { tpa_name: string; cashless?: number | null; logo_url?: string | null; }
interface ProofNumber { value: string; label: string; }
interface HospitalTemplateData {
  hospital: { name?: string; tagline?: string; address?: string; phone?: string; whatsapp?: string; hours_opd?: string; hours_emergency?: string; location_note?: string; established_year?: number; cta_text?: string; cta_sub_text?: string; };
  departments?: DepartmentItem[]; proof_numbers?: ProofNumber[];
  testimonials?: { total: number; average_rating?: number; samples: TestimonialSample[]; };
  accreditations?: AccredItem[]; facilities?: FacilityItem[]; insurance_partners?: InsurancePartnerItem[];
  finance?: { show_emi?: boolean; show_cashless?: boolean; disclaimer?: string; };
  display_toggles?: { show_finance?: boolean; show_accreditations?: boolean; show_faq?: boolean; show_location?: boolean; show_care_team?: boolean; sticky_cta?: boolean; };
}

// ─── Font & Global ────────────────────────────────────────────────────────
const fontStyles = (
  <GlobalStyles styles={`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');
    body { font-family: 'Inter', sans-serif; -webkit-font-smoothing: antialiased; }
    .glass-panel { background: rgba(255, 255, 255, 0.7); backdrop-filter: blur(24px); border: 1px solid rgba(255, 255, 255, 0.5); }
  `} />
);

const getIcon = (emoji: string) => {
  const map: Record<string, any> = { '❤️': HeartPulse, '🧠': Brain, '🦴': Bone, '👁️': Eye, '🦷': Sparkles, '👶': UserRound, '🏅': Award, '🏥': Building2, '🔬': Activity, '💊': Pill, '💳': CreditCard, '📱': Phone, '👨‍⚕️': Stethoscope };
  const Icon = map[emoji] || Activity;
  return <Icon size={24} />;
};

// ─── Components ───────────────────────────────────────────────────────────

function TrustBar() {
  return (
    <Box component={motion.div as any} initial={{ y: -50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5 }} sx={{ bgcolor: 'rgba(255, 255, 255, 0.85)', borderBottom: `1px solid ${C.border}`, backdropFilter: 'blur(12px)', px: { xs: 2, md: 3 }, py: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.6, flexWrap: 'wrap', position: 'sticky', top: 0, zIndex: 200 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 22, height: 22, bgcolor: C.navy, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ShieldCheck size={12} color="white" /></Box>
        <Typography sx={{ fontSize: 12, color: C.text2, fontWeight: 500 }}>Powered by <Box component="strong" sx={{ color: C.navy, fontWeight: 700 }}>Unofy Trust Link</Box></Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', bgcolor: C.tealLt, border: `1px solid ${C.teal}30`, borderRadius: '999px', px: '10px', py: '4px', fontSize: 10, fontWeight: 700, color: C.teal, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        <CheckCircle2 size={12} /> Verified Hospital Profile
      </Box>
    </Box>
  );
}

function HeroSection({ data, whatsappUrl, onVideoCall }: { data: HospitalTemplateData; whatsappUrl: string; onVideoCall: () => void }) {
  const { hospital, departments, testimonials, accreditations, proof_numbers } = data;
  const hospitalName = hospital?.name || 'Hospital';
  const tagline = hospital?.tagline || 'Experience world-class healthcare tailored to your distinct needs.';
  
  const defaultProof: ProofNumber[] = [
    ...(departments ? [{ value: `${departments.length}+`, label: 'Departments' }] : []),
    ...(testimonials ? [{ value: `${testimonials.total}+`, label: 'Patient Reviews' }] : []),
    ...(testimonials?.average_rating ? [{ value: `${testimonials.average_rating}★`, label: 'Rating' }] : []),
  ];
  const validProofNumbers = (proof_numbers || []).filter((p) => p && p.value);
  const proofItems = validProofNumbers.length > 0 ? validProofNumbers : defaultProof;

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden', bgcolor: C.bg, px: { xs: 2.5, md: '48px' }, pt: { xs: 8, md: '120px' }, pb: { xs: 6, md: '100px' } }}>
      <Box sx={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.05) 1px, transparent 0)', backgroundSize: '40px 40px', opacity: 0.6 }} />
      <Box sx={{ position: 'absolute', top: '-10%', left: '-10%', width: '60%', height: '60%', background: 'radial-gradient(circle, rgba(37, 99, 235,0.06) 0%, transparent 60%)', filter: 'blur(40px)' }} />

      <Box sx={{ position: 'relative', zIndex: 2, maxWidth: 1200, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 6, md: 8 }, alignItems: 'center' }}>
        <Box component={motion.div as any} initial="hidden" animate="visible" variants={stagger} sx={{ flex: 1 }}>
          <motion.div variants={fadeUp}><Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', bgcolor: C.white, border: `1px solid ${C.border}`, borderRadius: '999px', px: '14px', py: '6px', mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}><Activity size={14} color={C.text2} /><Typography sx={{ fontSize: 12, fontWeight: 600, color: C.text2, letterSpacing: '0.02em', textTransform: 'uppercase' }}>Premier Healthcare Center</Typography></Box></motion.div>
          <motion.div variants={fadeUp}>
            <Typography variant="h1" sx={{ fontSize: { xs: 44, md: 68 }, fontWeight: 800, color: C.navy, lineHeight: 1.05, mb: 3, letterSpacing: '-0.03em' }}>
              Welcome to <span style={{ background: `linear-gradient(135deg, ${C.teal} 0%, #06B6D4 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{hospitalName}</span>
            </Typography>
          </motion.div>
          <motion.div variants={fadeUp}><Typography sx={{ fontSize: { xs: 16, md: 18 }, color: C.text2, fontWeight: 400, lineHeight: 1.6, mb: 5, maxWidth: 480 }}>{tagline}</Typography></motion.div>
          <motion.div variants={fadeUp}>
            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              <Box component="a" href={whatsappUrl} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', bgcolor: C.navy, color: C.white, px: '28px', py: '16px', borderRadius: '16px', fontSize: 16, fontWeight: 700, textDecoration: 'none', transition: 'all 0.3s', boxShadow: `0 12px 24px -8px ${C.teal}`, '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 16px 32px -8px ${C.teal}`, bgcolor: C.teal } }}>
                Book Appointment <ArrowRight size={18} />
              </Box>
              {/* ── Video Consultation CTA ── */}
              <Box
                component="button"
                onClick={onVideoCall}
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  bgcolor: 'transparent', color: C.teal,
                  border: `2px solid ${C.teal}`,
                  px: '24px', py: '14px', borderRadius: '16px', fontSize: 15, fontWeight: 700,
                  cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif",
                  transition: 'all 0.3s',
                  '&:hover': { bgcolor: C.tealLt, transform: 'translateY(-2px)', boxShadow: `0 8px 20px -6px rgba(37,99,235,0.3)` },
                }}
              >
                <Video size={18} /> Video Consult
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Box sx={{ display: 'flex', '& > *': { ml: '-10px', border: `2px solid ${C.bg}`, borderRadius: '50%', width: 36, height: 36, bgcolor: C.tealLt, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.teal, fontSize: 13, fontWeight: 700 } }}>
                  <Box sx={{ ml: 0, backgroundImage: 'url(https://i.pravatar.cc/100?img=4)', backgroundSize: 'cover' }} />
                  <Box sx={{ backgroundImage: 'url(https://i.pravatar.cc/100?img=5)', backgroundSize: 'cover' }} />
                  <Box sx={{ backgroundImage: 'url(https://i.pravatar.cc/100?img=6)', backgroundSize: 'cover' }} />
                  <Box sx={{ bgcolor: C.white, border: `1px solid ${C.border}` }}>+</Box>
                </Box>
                <Typography sx={{ fontSize: 13, color: C.text2, fontWeight: 600, lineHeight: 1.2 }}>Trusted by<br/><Box component="span" sx={{ color: C.navy }}>100k+ patients</Box></Typography>
              </Box>
            </Box>
          </motion.div>
        </Box>
        <Box component={motion.div as any} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} sx={{ flex: 1, width: '100%' }}>
          <Box sx={{ position: 'relative' }}>
            <Box sx={{ position: 'absolute', inset: -20, background: 'linear-gradient(135deg, rgba(37,99,235,0.2) 0%, rgba(6,182,212,0.2) 100%)', filter: 'blur(30px)', borderRadius: '40px', zIndex: 0 }} />
            <Box className="glass-panel" sx={{ position: 'relative', zIndex: 1, borderRadius: '24px', p: 4, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 4, border: '1px solid rgba(255,255,255,0.4)', boxShadow: '0 24px 48px -12px rgba(0,0,0,0.1)' }}>
              {proofItems.slice(0, 4).map((item, i) => (
                <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: { xs: 32, md: 46 }, fontWeight: 800, color: C.navy, lineHeight: 1, display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                    {(item.value || '').replace(/[^0-9.]/g, '')}
                    <Box component="span" sx={{ color: C.teal, fontSize: '0.6em' }}>{(item.value || '').replace(/[0-9.]/g, '')}</Box>
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: C.text2, fontWeight: 500, letterSpacing: '0.02em', textTransform: 'uppercase' }}>{item.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function AccreditationsSection({ items }: { items: AccredItem[] }) {
  const active = items.filter(a => a.active);
  if (active.length === 0) return null;
  return (
    <Box sx={{ bgcolor: C.white, py: '40px', borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
      <Box component={motion.div as any} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10%" }} variants={stagger} sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: '48px' } }}>
        <Typography sx={{ textAlign: 'center', fontSize: 13, letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 800, color: C.text3, mb: 5 }}>Recognized by Global Standards</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: { xs: 3, md: 6 } }}>
          {active.map(a => (
            <motion.div key={a.id} variants={fadeUp}>
              <Box className="glass-panel" sx={{ display: 'flex', alignItems: 'center', gap: '12px', px: 3, py: 2, borderRadius: '16px', bgcolor: 'rgba(255,255,255,0.8)', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 30px rgba(0,0,0,0.06)', bgcolor: C.white } }}>
                <Award size={28} color={C.teal} />
                <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 700, color: C.navy }}>{a.name}</Typography>
              </Box>
            </motion.div>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function DepartmentsSection({ departments }: { departments: DepartmentItem[] }) {
  if (!departments || departments.length === 0) return null;
  return (
    <Box sx={{ py: '80px', bgcolor: C.bg }}>
      <Box component={motion.div as any} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10%" }} variants={stagger} sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: '48px' } }}>
        <Typography sx={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, color: C.teal, mb: '12px' }}>Specialities</Typography>
        <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: { xs: 28, md: 40 }, fontWeight: 800, color: C.navy, mb: '40px' }}>Centres of Excellence</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(3,1fr)' }, gap: 3 }}>
          {departments.map((d) => (
            <Box component={motion.div as any} variants={fadeUp} key={d.id} sx={{ position: 'relative', overflow: 'hidden', border: `1px solid ${C.border}`, borderRadius: '24px', p: '32px', bgcolor: C.white, transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', cursor: 'pointer', '&:hover': { transform: 'translateY(-6px)', boxShadow: `0 24px 48px -12px ${C.border}`, borderColor: 'rgba(37,99,235,0.3)', '& .proc-arrow': { transform: 'translateX(4px)', color: C.teal } }}}>
              <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: C.tealLt, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '24px', color: C.teal, transition: 'all 0.3s' }}>
                {getIcon(d.emoji || '')}
              </Box>
              <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 20, fontWeight: 800, color: C.navy, mb: '16px' }}>{d.name}</Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto', borderTop: `1px solid ${C.border}`, pt: 3 }}>
                <Box sx={{ display: 'flex', gap: '16px' }}>
                  {d.doctor_count && d.doctor_count > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography sx={{ fontSize: 11, color: C.text3, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>Doctors</Typography>
                      <Typography sx={{ fontSize: 16, fontWeight: 800, color: C.navy }}>{d.doctor_count}</Typography>
                    </Box>
                  ) : <Box />}
                  {d.procedure_count && d.procedure_count > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography sx={{ fontSize: 11, color: C.text3, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>Procedures</Typography>
                      <Typography sx={{ fontSize: 16, fontWeight: 800, color: C.navy }}>{d.procedure_count}</Typography>
                    </Box>
                  ) : <Box />}
                </Box>
                <ArrowRight className="proc-arrow" size={20} color={C.text3} style={{ transition: 'all 0.3s' }} />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function InsuranceSection({ partners, finance }: { partners?: InsurancePartnerItem[]; finance?: HospitalTemplateData['finance'] }) {
  if (!partners || partners.length === 0) return null;
  return (
    <Box sx={{ py: '80px', bgcolor: C.white, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
      <Box component={motion.div as any} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10%" }} variants={stagger} sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: '48px' } }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 6, md: 8 } }}>
          <Box component={motion.div as any} variants={fadeUp} sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, color: C.teal, mb: '12px' }}>Billing &amp; Finance</Typography>
            <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: { xs: 32, md: 48 }, fontWeight: 800, color: C.navy, mb: '20px', lineHeight: 1.1 }}>Cashless Treatment Available</Typography>
            <Typography sx={{ fontSize: 18, color: C.text2, mb: '32px' }}>We are empanelled with {partners.filter(p => p.cashless).length > 0 ? `${partners.filter(p => p.cashless).length}+` : 'multiple'} insurance companies to provide you a seamless cashless experience.</Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
              {finance?.show_cashless !== false && (
                <Box sx={{ bgcolor: C.bg, p: '24px', borderRadius: '20px', border: `1px solid ${C.border}` }}>
                  <CreditCard size={28} color={C.teal} style={{ marginBottom: '16px' }} />
                  <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 18, fontWeight: 700, color: C.navy, mb: '8px' }}>Cashless</Typography>
                  <Typography sx={{ fontSize: 14, color: C.text2 }}>Direct settlement with selected TPAs.</Typography>
                </Box>
              )}
              {finance?.show_emi !== false && (
                <Box sx={{ bgcolor: C.bg, p: '24px', borderRadius: '20px', border: `1px solid ${C.border}` }}>
                  <Banknote size={28} color={C.teal} style={{ marginBottom: '16px' }} />
                  <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 18, fontWeight: 700, color: C.navy, mb: '8px' }}>EMI Options</Typography>
                  <Typography sx={{ fontSize: 14, color: C.text2 }}>0% interest EMIs with our partners.</Typography>
                </Box>
              )}
            </Box>
          </Box>
          <Box component={motion.div as any} variants={fadeUp} sx={{ flex: 1 }}>
             <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
               {partners.slice(0, 9).map((p, i) => (
                 <Box key={i} sx={{ bgcolor: C.bg, border: `1px solid ${C.border}`, borderRadius: '16px', p: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', transition: 'all 0.2s', '&:hover': { transform: 'scale(1.05)', boxShadow: '0 10px 20px rgba(0,0,0,0.05)' } }}>
                   <ShieldCheck size={24} color={C.text3} style={{ marginBottom: '12px' }} />
                   <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.navy }}>{p.tpa_name}</Typography>
                 </Box>
               ))}
             </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function TestimonialsSection({ testimonials }: { testimonials: TestimonialSample[] }) {
  if (!testimonials || testimonials.length === 0) return null;
  return (
    <Box sx={{ py: '80px', bgcolor: C.bg }}>
      <Box component={motion.div as any} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10%" }} variants={stagger} sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: '48px' } }}>
        <Typography sx={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, color: C.teal, mb: '12px' }}>Testimonials</Typography>
        <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: { xs: 28, md: 40 }, fontWeight: 800, color: C.navy, mb: '40px' }}>What Our Patients Say</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2,1fr)' }, gap: 3 }}>
          {testimonials.map((t, i) => (
            <Box component={motion.div as any} variants={fadeUp} key={i} sx={{ border: `1px solid ${C.border}`, borderRadius: '20px', p: '32px', bgcolor: C.white, transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 20px 40px -10px rgba(0,0,0,0.08)' }}}>
              <MessageSquareQuote size={32} color={C.teal} style={{ marginBottom: '16px', opacity: 0.2 }} />
              <Typography sx={{ fontSize: 16, color: C.text, lineHeight: 1.6, mb: '24px', fontStyle: 'italic' }}>"{t.quote}"</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.white, fontWeight: 700 }}>
                  {t.name.charAt(0)}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: C.navy }}>{t.name}</Typography>
                  <Typography sx={{ fontSize: 13, color: C.text2 }}>{t.procedure || 'Patient'}</Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function FinalSection({ whatsappUrl, txt }: { whatsappUrl: string; txt?: string }) {
  return (
    <Box sx={{ py: '100px', bgcolor: C.navy, textAlign: 'center', px: 3 }}>
      <Box component={motion.div as any} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} sx={{ maxWidth: 640, mx: 'auto' }}>
        <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: { xs: 36, md: 52 }, fontWeight: 800, color: C.white, mb: '24px', lineHeight: 1.1 }}>Ready to get better?</Typography>
        <Typography sx={{ fontSize: 16, color: 'rgba(255,255,255,0.7)', mb: '40px' }}>{txt || 'Book a consultation today and begin your care journey with us.'}</Typography>
        <Box component="a" href={whatsappUrl} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', bgcolor: C.white, color: C.navy, px: '32px', py: '16px', borderRadius: '16px', fontSize: 16, fontWeight: 700, textDecoration: 'none', transition: 'all 0.2s', '&:hover': { transform: 'scale(1.02)' } }}>
          <CalendarCheck size={20} /> Book Appointment
        </Box>
      </Box>
    </Box>
  );
}

function FloatingCta({ whatsappUrl, ctaText }: { whatsappUrl: string; ctaText: string }) {
  return (
    <Box component={motion.div as any} initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }} sx={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 100 }}>
      <Box component="a" href={whatsappUrl} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: '12px', bgcolor: C.navy, color: C.white, pl: '20px', pr: '8px', py: '8px', borderRadius: '999px', textDecoration: 'none', boxShadow: '0 20px 40px -10px rgba(0,0,0,0.3)', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 24px 48px -12px rgba(0,0,0,0.4)', bgcolor: C.navy2 } }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{ctaText || 'Chat with Us'}</Typography>
        <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <MessageSquareQuote size={20} />
        </Box>
      </Box>
    </Box>
  );
}

export default function HospitalLinkTemplate({ initialData }: { initialData?: HospitalTemplateData }) {
  const [data, setData] = useState<HospitalTemplateData | null>(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);

  useEffect(() => {
    if (initialData) return;
    hospitalApiService.getHospitalPreview()
      .then((res) => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [initialData]);

  if (loading) return <Box sx={{ bgcolor: C.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{fontStyles}<CircularProgress sx={{ color: C.teal }} /></Box>;
  if (error || !data) return <Box sx={{ bgcolor: C.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>{fontStyles}<Typography fontSize={48}>😕</Typography><Typography sx={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 24, fontWeight: 800, color: C.navy }}>Link not found</Typography></Box>;

  const { hospital, departments, accreditations, insurance_partners, finance, display_toggles } = data;
  const toggles = display_toggles || {};
  const whatsappUrl = hospital?.whatsapp ? `https://wa.me/${hospital.whatsapp.replace(/\D/g, '')}` : '#';
  const testimonialList = data.testimonials?.samples || [];

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100vh' }}>
      {fontStyles}
      <TrustBar />
      <HeroSection data={data} whatsappUrl={whatsappUrl} onVideoCall={() => setVideoOpen(true)} />
      {toggles.show_accreditations !== false && (accreditations || []).length > 0 && <AccreditationsSection items={accreditations || []} />}
      <DepartmentsSection departments={departments || []} />
      <TestimonialsSection testimonials={testimonialList} />
      {toggles.show_finance !== false && <InsuranceSection partners={insurance_partners} finance={finance} />}
      <FinalSection whatsappUrl={whatsappUrl} txt={hospital?.cta_sub_text} />
      <Box sx={{ py: 4, textAlign: 'center', bgcolor: C.navy, borderTop: `1px solid ${C.navy3}` }}>
        <Typography sx={{ fontSize: 13, color: C.text3 }}>&copy; {new Date().getFullYear()} {hospital?.name}. All rights reserved.</Typography>
      </Box>
      {toggles.sticky_cta !== false && <FloatingCta whatsappUrl={whatsappUrl} ctaText={hospital?.cta_text || 'Book Appointment'} />}
      {/* ── Whereby Video Consultation Modal ── */}
      <VideoConsultModal
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        context="hospital-consult"
        hospitalName={hospital?.name}
      />
    </Box>
  );
}
